import { memo, useEffect, useRef, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { cn } from '@divlab/divanui';

import styles from './ProgressLine.module.css';

import type { FC, HTMLAttributes } from 'react';

export interface ProgressLineProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
}

// Задержка перед тем как начать показывать индикатор загрузки
// Используется для того, чтобы не отвлекать пользователя в тех случаях,
// когда страница загрузилась быстро
const delayBeforeVisible = 800;

// Списко ключей обязательных для загрузки страницы
const pageKeys = [
  'page',
  'translation',
  'infinitePromoCategory',
  'infiniteSearch',
  'compare',
  'profile',
  'orders',
  'complaints',
  'bonus-history',
  'meta',
  'layout',
  'promotions',
  'category-promotion',
  'info-stripe',
  'allRegions',
  'order-sections',
];

const ProgressLine: FC<ProgressLineProps> = (props) => {
  const { className, ...restProps } = props;
  const [visible, setVisible] = useState(false);
  const [width, setWidth] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const count = useIsFetching({
    predicate: (query) => query.queryKey.some((key: string) => pageKeys.includes(key)),
  });

  useEffect(() => {
    setWidth(100 / (count + 1));
  }, [count, visible]);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, delayBeforeVisible);

    return () => clearTimeout(timeoutRef.current);
  }, [count]);

  useEffect(() => {
    if (count !== 0) return;

    const id = setTimeout(() => {
      setVisible(false);
    }, 200);

    clearTimeout(timeoutRef.current);

    return () => clearTimeout(id);
  }, [count]);

  if (!visible) return null;

  return (
    <div {...restProps} className={cn(styles.line, className)}>
      <div className={styles.thumb} style={{ width: `${width}%` }} />
    </div>
  );
};

export default memo(ProgressLine);
